import { ConnectionHandler, User } from '@sendbird/chat';
import {
  GroupChannel,
  GroupChannelHandler,
  GroupChannelListQuery,
} from '@sendbird/chat/groupChannel';
import { AdminMessage, FileMessage, UserMessage } from '@sendbird/chat/message';
import { useCallback, useEffect, useMemo, useState } from 'react';
import sb from '~/services/sendbird/config';

import { getSendbirdData } from '~/services/localStorage/auth';
import { useStoreActions, useStoreState } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error/error';
import useRole from '../../../../store/user/hooks/useRole';

interface IUseChannels {
  loading: boolean;
  next: () => void;
  refresh: () => void;
}

const CHANNEL_HANDLER_KEY = 'channels';
let channelSubscribers = 0;

const useChannels = (): IUseChannels => {
  const [query, setQuery] = useState<GroupChannelListQuery | null>(null);

  const { sendBirdUserId } = useStoreState(state => state.user.current);
  const loading = useStoreState(state => state.coordinate.channelsLoading);

  const { accessToken } = getSendbirdData() || {};
  const { isActTeam, isGlobalUser } = useRole();

  const { setChannels, setChannelsLoading, updateChannel, updateMessage } = useStoreActions(
    actions => actions.coordinate,
  );
  const { showError } = useStoreActions(actions => actions.snackbar);

  const refresh = useCallback(() => {
    if (!sb) {
      return;
    }
    const newQuery = sb.groupChannel.createMyGroupChannelListQuery({
      limit: 20,
    });
    if (newQuery) {
      setQuery(newQuery);
    }
  }, []);

  const next = useCallback(async () => {
    if (query && query.hasNext && !query.isLoading) {
      setChannelsLoading(true);
      try {
        const fetchedChannels = await query.next();
        setChannels(fetchedChannels);
      } catch (error) {
        showError(extractErrorMessage(error));
      } finally {
        setChannelsLoading(false);
      }
    }
  }, [query, setChannels, setChannelsLoading, showError]);

  const currentUser: User | null = useMemo(() => sb?.currentUser, []);

  /// on channels event
  const channelHandler = useMemo(() => new GroupChannelHandler(), []);

  if (channelHandler) {
    channelHandler.onUserJoined = (channel, user) => {
      if (user.userId === currentUser?.userId) {
        updateChannel(channel);
      }
    };

    channelHandler.onMessageReceived = channel => {
      updateChannel(channel as GroupChannel);
    };

    channelHandler.onMessageUpdated = (_channel, message) => {
      updateMessage(message as UserMessage | FileMessage | AdminMessage);
    };
  }

  /// on connection event
  const connectionHandler = useMemo(() => new ConnectionHandler(), []);

  if (connectionHandler) {
    connectionHandler.onReconnectSucceeded = () => {
      refresh();
    };
  }

  const connect = useCallback(async () => {
    if (accessToken) {
      try {
        await sb.connect(sendBirdUserId, accessToken);
        refresh();
      } catch (e) {
        showError(extractErrorMessage(e));
      }
    }
  }, [accessToken, refresh, sendBirdUserId, showError]);

  useEffect(() => {
    if (!sb || (!isActTeam && !isGlobalUser) || !sendBirdUserId) {
      return undefined;
    }

    channelSubscribers += 1;
    const isFirstSubscriber = channelSubscribers === 1;

    if (isFirstSubscriber) {
      if (channelHandler) {
        sb.groupChannel.addGroupChannelHandler(CHANNEL_HANDLER_KEY, channelHandler);
      }
      if (connectionHandler) {
        sb.addConnectionHandler(CHANNEL_HANDLER_KEY, connectionHandler);
      }
      connect();
    }

    return () => {
      channelSubscribers -= 1;
      if (channelSubscribers === 0) {
        sb.removeConnectionHandler(CHANNEL_HANDLER_KEY);
        sb.groupChannel.removeGroupChannelHandler(CHANNEL_HANDLER_KEY);
      }
    };
  }, [
    refresh,
    channelHandler,
    connectionHandler,
    sendBirdUserId,
    isActTeam,
    isGlobalUser,
    connect,
    showError,
  ]);

  useEffect(() => {
    if (query && sb?.currentUser) {
      next();
    }
  }, [query, next]);

  return { loading, next, refresh };
};

export default useChannels;
